import React, { useEffect, useMemo, useRef, useState } from 'react';
import { usePacientes } from '../context/PatientsContext';
import { useAutenticacion } from '../context/AuthContext';
import { obtenerToken, resolverApiUrl } from '../services/api';
import {
  validarCuit,
  validarDni,
  validarDomicilio,
  validarFechaNacimiento,
} from '../utils/patientValidation';
import InputFecha from '../components/InputFecha';

const MESES = [
  'Enero',
  'Febrero',
  'Marzo',
  'Abril',
  'Mayo',
  'Junio',
  'Julio',
  'Agosto',
  'Septiembre',
  'Octubre',
  'Noviembre',
  'Diciembre',
];
const ANIO_BASE = new Date().getFullYear();
const SECCIONES_DETALLE = [
  { id: 'paciente', label: 'Paciente', icon: 'badge' },
  { id: 'familia', label: 'Escuela y familia', icon: 'family_restroom' },
  { id: 'cobertura', label: 'Obra social', icon: 'health_and_safety' },
  { id: 'tratamientos', label: 'Tratamientos', icon: 'medical_services' },
  { id: 'solicitudes', label: 'Autorizaciones', icon: 'clinical_notes' },
  { id: 'horarios', label: 'Horarios', icon: 'calendar_month' },
];
const DIAS_SEMANA = ['Dom', 'Lun', 'Mar', 'Mie', 'Jue', 'Vie', 'Sab'];
const DIAS = ['Lun', 'Mar', 'Mie', 'Jue', 'Vie'];
const ETIQUETAS_DIAS = {
  Lun: 'Lun',
  Mar: 'Mar',
  Mie: 'Mie',
  Jue: 'Jue',
  Vie: 'Viernes',
};
const ORDEN_DIAS_RESUMEN = ['Lun', 'Mar', 'Mie', 'Jue', 'Vie', 'Sab', 'Dom'];
const TRATAMIENTOS_BASE = [
  'Fonoaudiologia',
  'Psicologia',
  'Psicopedagogia',
  'Psicomotricidad',
  'Kinesiologia',
  'TO Terapia Ocupacional',
  'Integracion',
];
const MODULOS_PACIENTE = [
  { id: 'MII', label: 'Modulo integral intensivo (MII)' },
  { id: 'MIS', label: 'Modulo integral simple (MIS)' },
  { id: 'MIE', label: 'Modulo integracion escolar (MIE)' },
];
const ANIOS_ESCOLARES = ['2026', '2027', '2028', '2029', '2030'];

const normalizarModulosPaciente = (valor) => {
  const lista = Array.isArray(valor)
    ? valor
    : String(valor || '')
        .split(',')
        .map((item) => String(item || '').trim().toUpperCase())
        .filter(Boolean);
  return Array.from(new Set(lista.filter((item) => ['MII', 'MIS', 'MIE'].includes(item))));
};

const normalizarAniosEscolares = (valor) => {
  const lista = Array.isArray(valor)
    ? valor
    : String(valor || '')
        .split(',')
        .map((item) => String(item || '').trim())
        .filter(Boolean);
  return Array.from(new Set(lista.filter((item) => ANIOS_ESCOLARES.includes(item))));
};

const generarHorarios = () => {
  const horarios = [];
  const inicio = 8 * 60 + 15;
  const fin = 19 * 60 + 30;
  const paso = 45;
  for (let t = inicio; t <= fin; t += paso) {
    const h = String(Math.floor(t / 60)).padStart(2, '0');
    const m = String(t % 60).padStart(2, '0');
    horarios.push(`${h}:${m}`);
  }
  return horarios;
};

const HORARIOS = generarHorarios();
const HORARIOS_EXTRA_ALTA = [
  '08:00',
  '09:00',
  '10:00',
  '11:00',
  '12:00',
  '14:00',
  '15:00',
  '16:00',
  '17:00',
  '18:00',
  '19:00',
  '09:15',
  '09:45',
  '10:45',
  '14:30',
  '14:45',
  '15:15',
  '15:30',
  '16:15',
  '16:45',
  '17:30',
  '17:45',
  '18:15',
  '18:30',
  '19:45',
];

function resumirHorarioSemanal(turnosPorMes = {}, tratamientos = []) {
  const conteos = {};
  tratamientos.forEach((tratamiento) => {
    conteos[tratamiento] = {};
  });
  Object.values(turnosPorMes || {}).forEach((porTratamiento) => {
    Object.entries(porTratamiento || {}).forEach(([tratamiento, lista]) => {
      if (!conteos[tratamiento]) conteos[tratamiento] = {};
      (Array.isArray(lista) ? lista : []).forEach((clave) => {
        const [dia, hora] = String(clave || '').split('-');
        if (!DIAS.includes(dia) || !hora) return;
        if (!conteos[tratamiento][dia]) conteos[tratamiento][dia] = {};
        conteos[tratamiento][dia][hora] = (conteos[tratamiento][dia][hora] || 0) + 1;
      });
    });
  });
  const resultado = {};
  Object.entries(conteos).forEach(([tratamiento, porDia]) => {
    resultado[tratamiento] = {};
    Object.entries(porDia).forEach(([dia, porHora]) => {
      const mejor = Object.entries(porHora).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))[0];
      if (mejor) resultado[tratamiento][dia] = mejor[0];
    });
  });
  return resultado;
}

function serializarHorarioSemanal(horarios = {}, tratamientos = []) {
  return tratamientos.flatMap((tratamiento) =>
    DIAS.map((dia) => ({
      tratamiento,
      dia,
      hora: String(horarios?.[tratamiento]?.[dia] || '').trim(),
    })).filter((item) => item.hora)
  );
}

function describirHorarioSemanal(turnosPorMes = {}, tratamientos = []) {
  const horario = resumirHorarioSemanal(turnosPorMes, tratamientos);
  return tratamientos
    .map((tratamiento) => {
      const dias = DIAS.map((dia) =>
        horario?.[tratamiento]?.[dia] ? `${dia} ${horario[tratamiento][dia]}` : ''
      ).filter(Boolean);
      return dias.length ? `${tratamiento}: ${dias.join(', ')}` : '';
    })
    .filter(Boolean)
    .join(' · ');
}
const HORARIOS_POR_OBRA_SOCIAL = {
  ospe: ['8', '9', '10', '11', '12', '14', '15', '16', '17', '18', '19'],
  galeno: ['9', '9.45', '14.45', '15.30', '16.15', '17', '17.45', '18.30'],
  osolsac: [
    '9.15',
    '10',
    '10.45',
    '14.30',
    '15.15',
    '16',
    '16.45',
    '17.30',
    '18.15',
    '19',
    '19.45',
  ],
  omin: ['14', '14.45', '15.30'],
};
const normalizarTexto = (valor) =>
  String(valor || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
const toHora = (valor) => {
  const raw = String(valor || '').trim().replace('.', ':');
  if (!raw) return '';
  if (/^\d{1,2}$/.test(raw)) return `${String(Number(raw)).padStart(2, '0')}:00`;
  const m = raw.match(/^(\d{1,2}):(\d{1,2})$/);
  if (!m) return '';
  const hh = Number(m[1]);
  const mm = Number(m[2]);
  if (!Number.isInteger(hh) || !Number.isInteger(mm)) return '';
  if (hh < 0 || hh > 23 || mm < 0 || mm > 59) return '';
  return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
};


const minHora = (hora) => {
  const m = String(hora || '').trim().match(/^(\d{2}):(\d{2})$/);
  if (!m) return Number.MAX_SAFE_INTEGER;
  return Number(m[1]) * 60 + Number(m[2]);
};
const resolverHorariosPorObraSocial = (obraSocialId) => {
  const base = [...HORARIOS, ...HORARIOS_EXTRA_ALTA];
  const os = normalizarTexto(obraSocialId);
  let extras = [];
  if (os.includes('osolsac')) extras = HORARIOS_POR_OBRA_SOCIAL.osolsac;
  else if (os.includes('galeno')) extras = HORARIOS_POR_OBRA_SOCIAL.galeno;
  else if (os.includes('omin') || os.includes('omint')) extras = HORARIOS_POR_OBRA_SOCIAL.omin;
  else if (os === 'ospe' || os.startsWith('ospe ')) extras = HORARIOS_POR_OBRA_SOCIAL.ospe;
  const normalizados = extras.map(toHora).filter(Boolean);
  const set = new Set([...base, ...normalizados]);
  return Array.from(set).sort((a, b) => minHora(a) - minHora(b));
};
const normalizarHora = (valor) => {
  const m = String(valor || '').trim().match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (!m) return String(valor || '').trim();
  return `${String(Number(m[1])).padStart(2, '0')}:${m[2]}`;
};
const normalizarBooleano = (valor) => {
  if (typeof valor === 'boolean') return valor;
  const num = Number(valor);
  if (Number.isFinite(num)) return num !== 0;
  const txt = String(valor || '').trim().toLowerCase();
  return ['true', 't', 'si', 'on', '1'].includes(txt);
};

const normalizarFechaParaInput = (valor) => {
  if (!valor) return '';
  const raw = String(valor).trim();
  if (!raw || raw === '-') return '';
  const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})(?:$|[T\s])/);
  if (match) {
    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    const date = new Date(Date.UTC(year, month - 1, day));
    if (
      Number.isFinite(date.getTime()) &&
      date.getUTCFullYear() === year &&
      date.getUTCMonth() + 1 === month &&
      date.getUTCDate() === day
    ) {
      return `${match[1]}-${match[2]}-${match[3]}`;
    }
    return '';
  }
  const date = new Date(raw);
  if (!Number.isFinite(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
};

const formatearFechaArgentina = (valor) => {
  const iso = normalizarFechaParaInput(valor);
  if (!iso) return '-';
  const [anio, mes, dia] = iso.split('-');
  return `${dia}/${mes}/${anio}`;
};

const ordenarDiasResumen = (dias = []) => {
  return Array.from(dias).sort((a, b) => {
    const ia = ORDEN_DIAS_RESUMEN.indexOf(String(a));
    const ib = ORDEN_DIAS_RESUMEN.indexOf(String(b));
    const va = ia === -1 ? 999 : ia;
    const vb = ib === -1 ? 999 : ib;
    if (va !== vb) return va - vb;
    return String(a).localeCompare(String(b));
  });
};

export default function DetallePaciente({ alVolver, soloLectura = false }) {
  const { usuario } = useAutenticacion();
  const {
    pacientes,
    obrasSociales,
    pacienteSeleccionado,
    refrescarPacientes,
    actualizarPaciente,
    eliminarPaciente,
    agregarTratamientos,
    eliminarTratamiento,
    guardarExcepcionTurno,
    guardarSolicitudPaciente,
    cambiarEstadoSolicitudPaciente,
    cambiarFechasSolicitudPaciente,
    eliminarSolicitudPaciente,
    guardarHorariosSolicitudPaciente,
    guardarCronogramaPaciente,
  } = usePacientes();
  const [nombre, setNombre] = useState('');
  const [apellido, setApellido] = useState('');
  const [fechaNacimiento, setFechaNacimiento] = useState('');
  const [obraSocial, setObraSocial] = useState('');
  const [obraSocialPdfId, setObraSocialPdfId] = useState('');
  const [ultimoControlFisiatrico, setUltimoControlFisiatrico] = useState('');
  const [fechaAltaControlFisiatrico, setFechaAltaControlFisiatrico] = useState('');
  const [fechaVencimientoControlFisiatrico, setFechaVencimientoControlFisiatrico] = useState('');
  const [ultimoControlTrabajoSocial, setUltimoControlTrabajoSocial] = useState('');
  const [fechaAltaControlTrabajoSocial, setFechaAltaControlTrabajoSocial] = useState('');
  const [fechaVencimientoControlTrabajoSocial, setFechaVencimientoControlTrabajoSocial] = useState('');
  const [dni, setDni] = useState('');
  const [cuit, setCuit] = useState('');
  const [nroAfiliado, setNroAfiliado] = useState('');
  const [diagnostico, setDiagnostico] = useState('');
  const [modulo, setModulo] = useState([]);
  const [padreTutor, setPadreTutor] = useState('');
  const [telefonoPadreTutor, setTelefonoPadreTutor] = useState('');
  const [madreTutora, setMadreTutora] = useState('');
  const [telefonoMadreTutora, setTelefonoMadreTutora] = useState('');
  const [calle, setCalle] = useState('');
  const [numeracion, setNumeracion] = useState('');
  const [barrio, setBarrio] = useState('');
  const [piso, setPiso] = useState('');
  const [sector, setSector] = useState('');
  const [escuela, setEscuela] = useState('');
  const [anioGrado, setAnioGrado] = useState('');
  const [turnoEscolar, setTurnoEscolar] = useState('');
  const [carAnios, setCarAnios] = useState([]);
  const [ppiAnios, setPpiAnios] = useState([]);
  const [actaAcuerdoAnios, setActaAcuerdoAnios] = useState([]);
  const [integracionHorario, setIntegracionHorario] = useState('');
  const [guardado, setGuardado] = useState(false);
  const [errorGuardado, setErrorGuardado] = useState('');
  const [seccionDetalleActiva, setSeccionDetalleActiva] = useState('paciente');
  const [mostrarHorarios, setMostrarHorarios] = useState(soloLectura);
  const [mesesSeleccionados, setMesesSeleccionados] = useState(() => new Set());
  const [mostrarCronogramaBase, setMostrarCronogramaBase] = useState(soloLectura);
  const [cronogramaGeneralDraft, setCronogramaGeneralDraft] = useState({});
  const [guardandoCronogramaGeneral, setGuardandoCronogramaGeneral] = useState(false);
  const [tratamientoOs, setTratamientoOs] = useState('');
  const [mesOs, setMesOs] = useState(() => new Date().getMonth() + 1);
  const [anioOs, setAnioOs] = useState(() => new Date().getFullYear());
  const [autorizadoDesde, setAutorizadoDesde] = useState('');
  const [autorizadoHasta, setAutorizadoHasta] = useState('');
  const [solicitudFechaInicio, setSolicitudFechaInicio] = useState('');
  const [solicitudFechaFin, setSolicitudFechaFin] = useState('');
  const [solicitudTratamientos, setSolicitudTratamientos] = useState(() => new Set());
  const [modoSolicitudTratamientos, setModoSolicitudTratamientos] = useState('mismos');
  const [aplicarCambiosSolicitud, setAplicarCambiosSolicitud] = useState(true);
  const [estadoAutorizacionSolicitud, setEstadoAutorizacionSolicitud] = useState('pendiente');
  const [mensajeSolicitud, setMensajeSolicitud] = useState('');
  const [errorSolicitud, setErrorSolicitud] = useState('');
  const [guardandoSolicitud, setGuardandoSolicitud] = useState(false);
  const [solicitudActualizandoId, setSolicitudActualizandoId] = useState(null);
  const [solicitudFechasEditandoId, setSolicitudFechasEditandoId] = useState(null);
  const [solicitudFechaInicioEditando, setSolicitudFechaInicioEditando] = useState('');
  const [solicitudFechaFinEditando, setSolicitudFechaFinEditando] = useState('');
  const [guardandoFechasSolicitud, setGuardandoFechasSolicitud] = useState(false);
  const [eliminandoSolicitudId, setEliminandoSolicitudId] = useState(null);
  const [solicitudHorarios, setSolicitudHorarios] = useState({});
  const [solicitudEditandoId, setSolicitudEditandoId] = useState(null);
  const [solicitudHorariosEditando, setSolicitudHorariosEditando] = useState({});
  const [guardandoHorariosSolicitud, setGuardandoHorariosSolicitud] = useState(false);
  const [panelSolicitudAbierto, setPanelSolicitudAbierto] = useState(false);
  const ultimoPacienteIdRef = useRef(null);

  useEffect(() => {
    if (!pacienteSeleccionado) return;
    const esMismoPaciente = ultimoPacienteIdRef.current === pacienteSeleccionado.id;
    ultimoPacienteIdRef.current = pacienteSeleccionado.id;

    setNombre(pacienteSeleccionado.nombre || '');
    setApellido(pacienteSeleccionado.apellido || '');
    setFechaNacimiento(normalizarFechaParaInput(pacienteSeleccionado.fechaNacimiento));
    const obraSocialActual = String(pacienteSeleccionado.obraSocial || '').trim();
    setObraSocial(obraSocialActual);
    const existePlantilla = (obrasSociales || []).some(
      (o) =>
        String(o?.id || '').trim() === obraSocialActual &&
        o?.hasTemplate !== false
    );
    setObraSocialPdfId(existePlantilla ? obraSocialActual : '');
    setUltimoControlFisiatrico(
      normalizarFechaParaInput(pacienteSeleccionado.ultimoControlFisiatrico)
    );
    setFechaAltaControlFisiatrico(
      normalizarFechaParaInput(pacienteSeleccionado.fechaAltaControlFisiatrico)
    );
    setFechaVencimientoControlFisiatrico(
      normalizarFechaParaInput(pacienteSeleccionado.fechaVencimientoControlFisiatrico)
    );
    setUltimoControlTrabajoSocial(
      normalizarFechaParaInput(pacienteSeleccionado.ultimoControlTrabajoSocial)
    );
    setFechaAltaControlTrabajoSocial(
      normalizarFechaParaInput(pacienteSeleccionado.fechaAltaControlTrabajoSocial)
    );
    setFechaVencimientoControlTrabajoSocial(
      normalizarFechaParaInput(pacienteSeleccionado.fechaVencimientoControlTrabajoSocial)
    );
    setDni(pacienteSeleccionado.dni || '');
    setCuit(pacienteSeleccionado.cuit || '');
    setNroAfiliado(pacienteSeleccionado.nroAfiliado || '');
    setDiagnostico(pacienteSeleccionado.diagnostico || '');
    setModulo(normalizarModulosPaciente(pacienteSeleccionado.modulos || pacienteSeleccionado.modulo));
    setPadreTutor(pacienteSeleccionado.padreTutor || '');
    setTelefonoPadreTutor(pacienteSeleccionado.telefonoPadreTutor || '');
    setMadreTutora(pacienteSeleccionado.madreTutora || '');
    setTelefonoMadreTutora(pacienteSeleccionado.telefonoMadreTutora || '');
    setCalle(pacienteSeleccionado.calle || '');
    setNumeracion(pacienteSeleccionado.numeracion || '');
    setBarrio(pacienteSeleccionado.barrio || '');
    setPiso(pacienteSeleccionado.piso || '');
    setSector(pacienteSeleccionado.sector || '');
    setEscuela(pacienteSeleccionado.escuela || '');
    setAnioGrado(pacienteSeleccionado.anioGrado || '');
    setTurnoEscolar(pacienteSeleccionado.turnoEscolar || '');
    setCarAnios(normalizarAniosEscolares(pacienteSeleccionado.carAnios));
    setPpiAnios(normalizarAniosEscolares(pacienteSeleccionado.ppiAnios));
    setActaAcuerdoAnios(normalizarAniosEscolares(pacienteSeleccionado.actaAcuerdoAnios));
    setIntegracionHorario(pacienteSeleccionado.integracionHorario || '');
    const listaTrat = Array.isArray(pacienteSeleccionado.tratamientos)
      ? pacienteSeleccionado.tratamientos
      : [];
    setAutorizadoDesde(normalizarFechaParaInput(pacienteSeleccionado.autorizadoDesde));
    setAutorizadoHasta(normalizarFechaParaInput(pacienteSeleccionado.autorizadoHasta));

    if (!esMismoPaciente) {
      setSolicitudFechaInicio('');
      setSolicitudFechaFin('');
      setSolicitudTratamientos(new Set(listaTrat));
      setModoSolicitudTratamientos('mismos');
      setAplicarCambiosSolicitud(true);
      setEstadoAutorizacionSolicitud('pendiente');
      setMensajeSolicitud('');
      setErrorSolicitud('');
      setGuardandoSolicitud(false);
      setSolicitudActualizandoId(null);
      setSolicitudFechasEditandoId(null);
      setSolicitudFechaInicioEditando('');
      setSolicitudFechaFinEditando('');
      setGuardandoFechasSolicitud(false);
      setEliminandoSolicitudId(null);
      setSolicitudHorarios(resumirHorarioSemanal(pacienteSeleccionado.turnosPorMes, listaTrat));
      setSolicitudEditandoId(null);
      setSolicitudHorariosEditando({});
      setGuardandoHorariosSolicitud(false);
      setPanelSolicitudAbierto(false);
      setMostrarHorarios(soloLectura);
      setMostrarCronogramaBase(soloLectura);
      setCronogramaGeneralDraft({});
      setTratamientoOs(listaTrat.length ? listaTrat[0] : '');
      setMesesSeleccionados(new Set());
    }
  }, [pacienteSeleccionado, obrasSociales, soloLectura]);

  useEffect(() => {
    const controller = new AbortController();
    refrescarPacientes(controller.signal).catch(() => {});
    return () => controller.abort();
  }, [refrescarPacientes]);

  const resumenTurnos = useMemo(() => {
    if (!pacienteSeleccionado) return {};
    const turnosPorMes = pacienteSeleccionado.turnosPorMes || {};
    const resultado = {};
    Object.keys(turnosPorMes).forEach((mesKey) => {
      const porTratamiento = turnosPorMes[mesKey] || {};
      Object.keys(porTratamiento).forEach((tratamiento) => {
        const lista = porTratamiento[tratamiento] || [];
        if (!lista.length) return;
        if (!resultado[tratamiento]) {
          resultado[tratamiento] = {
            meses: [],
            dias: new Set(),
            horas: new Set(),
          };
        }
        resultado[tratamiento].meses.push(Number(mesKey));
        lista.forEach((clave) => {
          const [dia, hora] = String(clave).split('-');
          if (dia) resultado[tratamiento].dias.add(dia);
          if (hora) resultado[tratamiento].horas.add(hora);
        });
      });
    });
    return resultado;
  }, [pacienteSeleccionado]);

  const overridesMap = useMemo(() => {
    const mapa = {};
    const lista = pacienteSeleccionado?.turnosOverrides || [];
    lista.forEach((o) => {
      if (!o.fecha || !o.tratamiento || !o.hora) return;
      const fecha = String(o.fecha).slice(0, 10);
      const hora = normalizarHora(o.hora);
      const activo = normalizarBooleano(o.activo);
      if (!fecha || !hora) return;
      if (!mapa[fecha]) mapa[fecha] = {};
      if (!mapa[fecha][o.tratamiento]) mapa[fecha][o.tratamiento] = {};
      const previo = mapa[fecha][o.tratamiento][hora];
      if (previo === undefined) {
        mapa[fecha][o.tratamiento][hora] = activo;
      } else {
        mapa[fecha][o.tratamiento][hora] = previo === false || activo === false ? false : true;
      }
    });
    return mapa;
  }, [pacienteSeleccionado]);
  const horariosDisponiblesPaciente = useMemo(
    () =>
      resolverHorariosPorObraSocial(
        String(obraSocialPdfId || obraSocial || pacienteSeleccionado?.obraSocial || '').trim()
      ),
    [obraSocialPdfId, obraSocial, pacienteSeleccionado?.obraSocial]
  );

  const obtenerCalendarioMes = (mes) => {
    const diasEnMes = new Date(ANIO_BASE, mes, 0).getDate();
    const semanas = [];
    let semana = [null, null, null, null, null];
    for (let dia = 1; dia <= diasEnMes; dia += 1) {
      const fecha = new Date(ANIO_BASE, mes - 1, dia);
      const dow = fecha.getDay();
      if (dow === 0 || dow === 6) continue;
      const idx = dow - 1;
      semana[idx] = dia;
      if (dow === 5) {
        semanas.push(semana);
        semana = [null, null, null, null, null];
      }
    }
    if (semana.some((d) => d !== null)) {
      semanas.push(semana);
    }
    return semanas;
  };

  const obtenerHorasBase = (mes, tratamiento) => {
    const turnosMes = (pacienteSeleccionado?.turnosPorMes || {})[mes] || {};
    const lista = turnosMes[tratamiento] || [];
    const porDia = {};
    lista.forEach((clave) => {
      const [dia, hora] = String(clave).split('-');
      if (!dia || !hora) return;
      if (!porDia[dia]) porDia[dia] = [];
      porDia[dia].push(normalizarHora(hora));
    });
    Object.keys(porDia).forEach((d) => porDia[d].sort());
    return porDia;
  };

  const obtenerHorasDia = (mes, tratamiento, diaNumero) => {
    const fecha = new Date(ANIO_BASE, mes - 1, diaNumero);
    const dow = fecha.getDay();
    const diaNombre = DIAS_SEMANA[dow];
    const base = obtenerHorasBase(mes, tratamiento);
    const horasBase = base[diaNombre] || [];
    const fechaStr = `${ANIO_BASE}-${String(mes).padStart(2, '0')}-${String(
      diaNumero
    ).padStart(2, '0')}`;
    const overridesTratamiento =
      overridesMap[fechaStr] && overridesMap[fechaStr][tratamiento]
        ? overridesMap[fechaStr][tratamiento]
        : {};
    const horas = new Set(horasBase);
    Object.keys(overridesTratamiento).forEach((h) => {
      const activo = overridesTratamiento[normalizarHora(h)];
      if (activo) {
        horas.add(normalizarHora(h));
      } else {
        horas.delete(normalizarHora(h));
      }
    });
    return Array.from(horas).sort();
  };

  const alternarExcepcion = (mes, tratamiento, diaNumero, hora, activo) => {
    const fechaStr = `${ANIO_BASE}-${String(mes).padStart(2, '0')}-${String(
      diaNumero
    ).padStart(2, '0')}`;
    guardarExcepcionTurno(
      pacienteSeleccionado.id,
      tratamiento,
      fechaStr,
      hora,
      activo
    );
  };

  const guardarCambios = async (e) => {
    e.preventDefault();
    if (!pacienteSeleccionado) return;
    setErrorGuardado('');
    if (!nombre.trim() || !apellido.trim()) {
      setSeccionDetalleActiva('paciente');
      setErrorGuardado('Completá el nombre y el apellido del paciente.');
      return;
    }
    const errorDatos =
      validarFechaNacimiento(fechaNacimiento) ||
      validarDni(dni) ||
      validarCuit(cuit) ||
      ((calle.trim() || String(pacienteSeleccionado.calle || '').trim())
        ? validarDomicilio(calle)
        : '');
    if (errorDatos) {
      setSeccionDetalleActiva('paciente');
      setErrorGuardado(errorDatos);
      return;
    }
    if (obraSocial.trim() && !obraSocialPdfId) {
      setSeccionDetalleActiva('cobertura');
      setErrorGuardado('Seleccioná la obra social desde la lista; escribir el nombre no alcanza.');
      return;
    }
    if (Boolean(autorizadoDesde) !== Boolean(autorizadoHasta)) {
      setSeccionDetalleActiva('paciente');
      setErrorGuardado('Completa la fecha de inicio y la fecha de fin de la autorización.');
      return;
    }
    if (autorizadoDesde && autorizadoHasta && autorizadoDesde > autorizadoHasta) {
      setSeccionDetalleActiva('paciente');
      setErrorGuardado('La fecha de fin de la autorización no puede ser anterior a la fecha de inicio.');
      return;
    }
    try {
      await actualizarPaciente(pacienteSeleccionado.id, {
        nombre: nombre.trim(),
        apellido: apellido.trim(),
        fechaNacimiento,
        obraSocial: obraSocial.trim(),
        ultimoControlFisiatrico,
        fechaAltaControlFisiatrico,
        fechaVencimientoControlFisiatrico,
        ultimoControlTrabajoSocial,
        fechaAltaControlTrabajoSocial,
        fechaVencimientoControlTrabajoSocial,
        dni: dni.trim(),
        cuit: cuit.trim(),
        nroAfiliado: nroAfiliado.trim(),
        numeroAfiliado: nroAfiliado.trim(),
        affiliateNumber: nroAfiliado.trim(),
        affiliate_number: nroAfiliado.trim(),
        diagnostico: diagnostico.trim(),
        modulo,
        modulos: modulo,
        padreTutor: padreTutor.trim(),
        telefonoPadreTutor: telefonoPadreTutor.trim(),
        madreTutora: madreTutora.trim(),
        telefonoMadreTutora: telefonoMadreTutora.trim(),
        calle: calle.trim(),
        numeracion: numeracion.trim(),
        barrio: barrio.trim(),
        piso: piso.trim(),
        sector: sector.trim(),
        escuela: escuela.trim(),
        anioGrado: anioGrado.trim(),
        turnoEscolar: turnoEscolar.trim(),
        carAnios,
        ppiAnios,
        actaAcuerdoAnios,
        integracionHorario: integracionHorario.trim(),
        autorizadoDesde: autorizadoDesde || null,
        autorizadoHasta: autorizadoHasta || null,
      });
      setGuardado(true);
      setTimeout(() => setGuardado(false), 2500);
    } catch (err) {
      const msg = String(err?.message || '').trim();
      setErrorGuardado(msg || 'No se pudieron guardar los cambios.');
    }
  };

  const confirmarEliminacion = () => {
    if (!pacienteSeleccionado) return;
    if (!usuario?.isAdmin) return;
    const ok = window.confirm('Estas seguro de eliminar este paciente?');
    if (!ok) return;
    eliminarPaciente(pacienteSeleccionado.id);
    alVolver();
  };

  const confirmarQuitarTratamiento = (tratamiento) => {
    if (!pacienteSeleccionado) return;
    const ok = window.confirm(
      `Estas seguro de quitar el tratamiento ${tratamiento}?`
    );
    if (!ok) return;
    eliminarTratamiento(pacienteSeleccionado.id, tratamiento);
  };

  const alternarMesSeleccionado = (mes) => {
    setMesesSeleccionados((prev) => {
      const nuevo = new Set(prev);
      if (nuevo.has(mes)) {
        nuevo.delete(mes);
      } else {
        nuevo.add(mes);
      }
      return nuevo;
    });
  };

  const tratamientosDisponibles = useMemo(() => {
    const set = new Set(TRATAMIENTOS_BASE);
    (pacienteSeleccionado?.tratamientos || []).forEach((t) => set.add(t));
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [pacienteSeleccionado]);

  const tratamientosSolicitudDisponibles = useMemo(() => {
    const set = new Set(TRATAMIENTOS_BASE);
    (pacienteSeleccionado?.tratamientos || []).forEach((t) => set.add(t));
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'es'));
  }, [pacienteSeleccionado?.tratamientos]);

  const historialSolicitudes = useMemo(() => {
    const lista = Array.isArray(pacienteSeleccionado?.solicitudes)
      ? pacienteSeleccionado.solicitudes
      : [];
    return lista
      .slice()
      .sort((a, b) => {
        const fa = String(a?.fechaInicio || '');
        const fb = String(b?.fechaInicio || '');
        return fb.localeCompare(fa, 'es');
      });
  }, [pacienteSeleccionado?.solicitudes]);

  const alternarTratamientoSolicitud = (tratamiento) => {
    setSolicitudTratamientos((prev) => {
      const next = new Set(prev);
      if (next.has(tratamiento)) next.delete(tratamiento);
      else next.add(tratamiento);
      return next;
    });
  };

  const actualizarHoraSolicitud = (setter, tratamiento, dia, hora) => {
    setter((prev) => ({
      ...prev,
      [tratamiento]: {
        ...(prev?.[tratamiento] || {}),
        [dia]: hora,
      },
    }));
  };

  const usarMismosTratamientosSolicitud = () => {
    const actuales = Array.isArray(pacienteSeleccionado?.tratamientos)
      ? pacienteSeleccionado.tratamientos
      : [];
    setModoSolicitudTratamientos('mismos');
    setSolicitudTratamientos(new Set(actuales));
    setSolicitudHorarios(resumirHorarioSemanal(pacienteSeleccionado?.turnosPorMes, actuales));
    setAplicarCambiosSolicitud(false);
    setMensajeSolicitud('');
    setErrorSolicitud('');
    setPanelSolicitudAbierto(true);
  };

  const usarNuevosTratamientosSolicitud = () => {
    const actuales = Array.isArray(pacienteSeleccionado?.tratamientos)
      ? pacienteSeleccionado.tratamientos
      : [];
    setModoSolicitudTratamientos('nuevos');
    setSolicitudTratamientos(new Set(actuales));
    setSolicitudHorarios(resumirHorarioSemanal(pacienteSeleccionado?.turnosPorMes, actuales));
    setAplicarCambiosSolicitud(true);
    setMensajeSolicitud('');
    setErrorSolicitud('');
    setPanelSolicitudAbierto(true);
  };

  const guardarSolicitud = async () => {
    if (!pacienteSeleccionado) return;
    setMensajeSolicitud('');
    setErrorSolicitud('');
    const inicio = String(solicitudFechaInicio || '').trim();
    const fin = String(solicitudFechaFin || '').trim();
    const tratamientos = Array.from(solicitudTratamientos);
    if (!inicio || !fin) {
      setErrorSolicitud('Completa inicio y fin de solicitud.');
      return;
    }
    if (fin < inicio) {
      setErrorSolicitud('La fecha de fin no puede ser anterior al inicio.');
      return;
    }
    const tratamientosFinales =
      modoSolicitudTratamientos === 'mismos'
        ? (Array.isArray(pacienteSeleccionado?.tratamientos)
          ? pacienteSeleccionado.tratamientos.slice()
          : [])
        : tratamientos;
    if (!tratamientosFinales.length) {
      setErrorSolicitud('Selecciona al menos una terapia para la solicitud.');
      return;
    }
    setGuardandoSolicitud(true);
    try {
      await guardarSolicitudPaciente(pacienteSeleccionado.id, {
        fechaInicio: inicio,
        fechaFin: fin,
        tratamientos: tratamientosFinales,
        aplicarTratamientos:
          modoSolicitudTratamientos === 'nuevos' && aplicarCambiosSolicitud,
        estadoAutorizacion: estadoAutorizacionSolicitud,
        horarios: serializarHorarioSemanal(solicitudHorarios, tratamientosFinales),
      });
      setSolicitudFechaInicio('');
      setSolicitudFechaFin('');
      setSolicitudTratamientos(new Set(tratamientosFinales));
      setModoSolicitudTratamientos('mismos');
      setAplicarCambiosSolicitud(true);
      setEstadoAutorizacionSolicitud('pendiente');
      setSolicitudHorarios(
        resumirHorarioSemanal(pacienteSeleccionado?.turnosPorMes, tratamientosFinales)
      );
      setMensajeSolicitud('Solicitud guardada correctamente.');
      setPanelSolicitudAbierto(false);
    } catch (err) {
      const msg = String(err?.message || '').trim();
      setErrorSolicitud(msg || 'No se pudo guardar la solicitud.');
    } finally {
      setGuardandoSolicitud(false);
    }
  };

  const cambiarEstadoAutorizacionSolicitud = async (solicitud, siguienteEstado) => {
    if (!pacienteSeleccionado || !solicitud?.id) return;
    setMensajeSolicitud('');
    setErrorSolicitud('');
    setSolicitudActualizandoId(solicitud.id);
    try {
      await cambiarEstadoSolicitudPaciente(
        pacienteSeleccionado.id,
        solicitud.id,
        siguienteEstado
      );
      setMensajeSolicitud(
        siguienteEstado === 'autorizada'
          ? 'La solicitud quedó autorizada.'
          : 'La solicitud volvió a estado pendiente.'
      );
    } catch (err) {
      setErrorSolicitud(String(err?.message || 'No se pudo actualizar la autorización.'));
    } finally {
      setSolicitudActualizandoId(null);
    }
  };

  const abrirEditorFechasSolicitud = (solicitud) => {
    if (!usuario?.isAdmin || !solicitud?.id) return;
    if (solicitudFechasEditandoId === solicitud.id) {
      setSolicitudFechasEditandoId(null);
      setSolicitudFechaInicioEditando('');
      setSolicitudFechaFinEditando('');
      return;
    }
    setSolicitudFechasEditandoId(solicitud.id);
    setSolicitudFechaInicioEditando(normalizarFechaParaInput(solicitud.fechaInicio));
    setSolicitudFechaFinEditando(normalizarFechaParaInput(solicitud.fechaFin));
    setSolicitudEditandoId(null);
    setSolicitudHorariosEditando({});
    setMensajeSolicitud('');
    setErrorSolicitud('');
  };

  const guardarFechasSolicitud = async (solicitud) => {
    if (!usuario?.isAdmin || !pacienteSeleccionado || !solicitud?.id) return;
    const inicio = String(solicitudFechaInicioEditando || '').trim();
    const fin = String(solicitudFechaFinEditando || '').trim();
    setMensajeSolicitud('');
    setErrorSolicitud('');
    if (!inicio || !fin) {
      setErrorSolicitud('Completa la fecha de inicio y la fecha de vencimiento.');
      return;
    }
    if (fin < inicio) {
      setErrorSolicitud(
        'La fecha de vencimiento no puede ser anterior al inicio. Si el período comienza en octubre y termina en septiembre, seleccioná septiembre del año siguiente.'
      );
      return;
    }
    setGuardandoFechasSolicitud(true);
    try {
      await cambiarFechasSolicitudPaciente(
        pacienteSeleccionado.id,
        solicitud.id,
        inicio,
        fin
      );
      setSolicitudFechasEditandoId(null);
      setSolicitudFechaInicioEditando('');
      setSolicitudFechaFinEditando('');
      setMensajeSolicitud('Fechas de la solicitud actualizadas correctamente.');
    } catch (err) {
      setErrorSolicitud(String(err?.message || 'No se pudieron actualizar las fechas.'));
    } finally {
      setGuardandoFechasSolicitud(false);
    }
  };

  const confirmarEliminarSolicitud = async (solicitud) => {
    if (!usuario?.isAdmin || !pacienteSeleccionado || !solicitud?.id) return;
    const periodo = `${formatearFechaArgentina(solicitud.fechaInicio)} al ${formatearFechaArgentina(
      solicitud.fechaFin
    )}`;
    const advertencia = solicitud.vigente
      ? '\n\nEsta solicitud está vigente. Al eliminarla se volverá a evaluar inmediatamente la autorización del paciente.'
      : '';
    if (!window.confirm(`¿Eliminar definitivamente la solicitud del ${periodo}?${advertencia}`)) {
      return;
    }
    setMensajeSolicitud('');
    setErrorSolicitud('');
    setEliminandoSolicitudId(solicitud.id);
    try {
      await eliminarSolicitudPaciente(pacienteSeleccionado.id, solicitud.id);
      setSolicitudFechasEditandoId(null);
      setSolicitudEditandoId(null);
      setMensajeSolicitud('Solicitud eliminada correctamente.');
    } catch (err) {
      setErrorSolicitud(String(err?.message || 'No se pudo eliminar la solicitud.'));
    } finally {
      setEliminandoSolicitudId(null);
    }
  };

  const abrirEditorHorariosSolicitud = (solicitud) => {
    setSolicitudFechasEditandoId(null);
    setSolicitudFechaInicioEditando('');
    setSolicitudFechaFinEditando('');
    if (solicitudEditandoId === solicitud.id) {
      setSolicitudEditandoId(null);
      setSolicitudHorariosEditando({});
      return;
    }
    const tieneHorariosGuardados = Object.keys(solicitud?.turnosPorMes || {}).length > 0;
    setSolicitudEditandoId(solicitud.id);
    setSolicitudHorariosEditando(
      resumirHorarioSemanal(
        tieneHorariosGuardados ? solicitud.turnosPorMes : pacienteSeleccionado?.turnosPorMes,
        solicitud.tratamientos || []
      )
    );
  };

  const guardarEditorHorariosSolicitud = async (solicitud) => {
    if (!pacienteSeleccionado || !solicitud?.id) return;
    setGuardandoHorariosSolicitud(true);
    setMensajeSolicitud('');
    setErrorSolicitud('');
    try {
      await guardarHorariosSolicitudPaciente(
        pacienteSeleccionado.id,
        solicitud.id,
        serializarHorarioSemanal(solicitudHorariosEditando, solicitud.tratamientos || [])
      );
      setSolicitudEditandoId(null);
      setSolicitudHorariosEditando({});
      setMensajeSolicitud('Horarios de la solicitud guardados sin modificar el cronograma vigente.');
    } catch (err) {
      setErrorSolicitud(String(err?.message || 'No se pudieron guardar los horarios.'));
    } finally {
      setGuardandoHorariosSolicitud(false);
    }
  };

  const diagnosticosDisponibles = useMemo(() => {
    const set = new Set();
    (pacientes || []).forEach((p) => {
      const valor = String(p?.diagnostico || '').trim();
      if (valor) set.add(valor);
    });
    const actual = String(diagnostico || '').trim();
    if (actual) set.add(actual);
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [pacientes, diagnostico]);

  const ordenarObrasSociales = useMemo(() => {
    return (obrasSociales || [])
      .filter((o) => o?.id)
      .slice()
      .sort((a, b) => String(a.id).localeCompare(String(b.id)));
  }, [obrasSociales]);

  const descargarObraSocial = async () => {
    if (!pacienteSeleccionado) return;
    const obraSocialId = String(obraSocialPdfId || '').trim();
    if (!obraSocialId) {
      const obraActual = String(obraSocial || '').trim();
      const obraConfig = (obrasSociales || []).find(
        (o) => String(o?.id || '').trim() === obraActual
      );
      if (obraConfig && obraConfig.hasTemplate === false) {
        window.alert('La obra social seleccionada existe, pero no tiene plantilla para imprimir.');
        return;
      }
      window.alert(
        'Selecciona una obra social desde "Lista de obras sociales (carpeta - CUIT)" para generar el PDF.'
      );
      return;
    }
    if (!String(tratamientoOs || '').trim()) {
      window.alert('Selecciona un tratamiento para generar la planilla.');
      return;
    }
    const mesNum = Number(mesOs);
    const anioNum = Number(anioOs);
    if (!Number.isInteger(mesNum) || mesNum < 1 || mesNum > 12) {
      window.alert('Mes inválido.');
      return;
    }
    if (!Number.isInteger(anioNum) || anioNum < 2000 || anioNum > 2100) {
      window.alert('Año inválido.');
      return;
    }
    const url = `/api/patients/${encodeURIComponent(
      pacienteSeleccionado.id
    )}/obras-sociales/${encodeURIComponent(obraSocialId)}?tratamiento=${encodeURIComponent(
      String(tratamientoOs).trim()
    )}&mes=${encodeURIComponent(String(mesNum))}&anio=${encodeURIComponent(
      String(anioNum)
    )}`;
    const finalUrl = resolverApiUrl(url) || url;
    const token = obtenerToken();
    const headers = {};
    if (token) headers.Authorization = `Bearer ${token}`;
    try {
      const respuesta = await fetch(finalUrl, {
        method: 'GET',
        headers,
        credentials: 'include',
      });
      if (!respuesta.ok) {
        let mensaje = `Error ${respuesta.status}`;
        try {
          const data = await respuesta.json();
          if (data && data.error) mensaje = data.error;
        } catch (err) {}
        window.alert(`No se pudo descargar el PDF: ${mensaje}`);
        return;
      }
      const blob = await respuesta.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const enlace = document.createElement('a');
      enlace.href = blobUrl;
      enlace.download = `${obraSocialId}.pdf`;
      document.body.appendChild(enlace);
      enlace.click();
      enlace.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      window.alert('No se pudo descargar el PDF. Revisa la conexión con el servidor.');
    }
  };

  const solicitudObjetivoCronograma = historialSolicitudes
    .filter(
      (solicitud) =>
        !solicitud?.vencida &&
        (solicitud?.futura || solicitud?.estadoAutorizacion === 'pendiente')
    )
    .sort((a, b) => String(a?.fechaInicio || '').localeCompare(String(b?.fechaInicio || '')))[0];
  const tratamientosCronograma = solicitudObjetivoCronograma
    ? solicitudObjetivoCronograma.tratamientos || []
    : pacienteSeleccionado?.tratamientos || [];
  const turnosPorMes = solicitudObjetivoCronograma
    ? solicitudObjetivoCronograma.turnosPorMes || {}
    : pacienteSeleccionado?.turnosPorMes || {};
  const horarioSemanalBaseCronograma = resumirHorarioSemanal(
    turnosPorMes,
    tratamientosCronograma
  );

  const obtenerMarcadoGeneral = (tratamiento, clave) => {
    const draft = cronogramaGeneralDraft[tratamiento];
    if (draft && Object.prototype.hasOwnProperty.call(draft, clave)) {
      return draft[clave];
    }
    const [dia, hora] = String(clave || '').split('-');
    return horarioSemanalBaseCronograma?.[tratamiento]?.[dia] === hora;
  };

  const alternarDraftGeneral = (tratamiento, clave) => {
    setCronogramaGeneralDraft((prev) => {
      const actual = obtenerMarcadoGeneral(tratamiento, clave);
      const siguiente = !actual;
      const copiaTrat = { ...(prev[tratamiento] || {}) };
      const [dia] = String(clave).split('-');
      if (siguiente && dia) {
        horariosDisponiblesPaciente.forEach((h) => {
          copiaTrat[`${dia}-${h}`] = false;
        });
      }
      copiaTrat[clave] = siguiente;
      return { ...prev, [tratamiento]: copiaTrat };
    });
  };

  const hayCambiosGeneral = Object.values(cronogramaGeneralDraft).some(
    (t) => Object.keys(t || {}).length
  );

  const guardarCronogramaGeneral = async () => {
    if (!pacienteSeleccionado) return;
    const horarioFinal = {};
    tratamientosCronograma.forEach((tratamiento) => {
      horarioFinal[tratamiento] = {};
      DIAS.forEach((dia) => {
        const hora = horariosDisponiblesPaciente.find((valor) =>
          obtenerMarcadoGeneral(tratamiento, `${dia}-${valor}`)
        );
        if (hora) horarioFinal[tratamiento][dia] = hora;
      });
    });
    const horarios = serializarHorarioSemanal(horarioFinal, tratamientosCronograma);
    setGuardandoCronogramaGeneral(true);
    try {
      if (solicitudObjetivoCronograma) {
        await guardarHorariosSolicitudPaciente(
          pacienteSeleccionado.id,
          solicitudObjetivoCronograma.id,
          horarios
        );
        setMensajeSolicitud(
          'Cronograma guardado únicamente en la solicitud; la autorización vigente no cambió.'
        );
      } else {
        await guardarCronogramaPaciente(pacienteSeleccionado.id, horarios);
      }
      setCronogramaGeneralDraft({});
    } catch (err) {
      window.alert(String(err?.message || 'No se pudo guardar el cronograma.'));
    } finally {
      setGuardandoCronogramaGeneral(false);
    }
  };

  const descartarCronogramaGeneral = () => {
    setCronogramaGeneralDraft({});
  };

  const alternarTratamientoPaciente = (tratamiento, activo) => {
    if (!pacienteSeleccionado || !tratamiento) return;
    const actuales = pacienteSeleccionado.tratamientos || [];
    if (activo) {
      if (actuales.includes(tratamiento)) return;
      agregarTratamientos(pacienteSeleccionado.id, [tratamiento]);
    } else {
      if (!actuales.includes(tratamiento)) return;
      confirmarQuitarTratamiento(tratamiento);
    }
  };

  if (!pacienteSeleccionado) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <div className="py-16 text-center rounded-2xl border-2 border-dashed border-surface-200 bg-surface-50/50">
          <p className="text-surface-600 font-medium">No hay paciente seleccionado.</p>
          <button
            onClick={alVolver}
            className="mt-4 px-4 py-2.5 rounded-xl text-sm font-medium bg-surface-200 text-surface-800 hover:bg-surface-300 transition-colors duration-200"
          >
            Volver
          </button>
        </div>
      </div>
    );
  }

  const inputBase = "w-full px-4 py-2.5 rounded-lg border border-surface-200 bg-white text-surface-800 placeholder:text-surface-400 focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500 transition-all duration-200";
  const labelBase = "block text-sm font-medium text-surface-700 mb-1.5";
  const seccionBase = "rounded-[28px] border border-surface-200/80 bg-gradient-to-br from-white to-surface-50/60 p-5 mb-5 shadow-sm shadow-black/5";
  const tituloSeccion = "text-sm font-semibold text-primary-700 uppercase tracking-wide mb-4 mt-0";
  const nombreCompletoPaciente = [pacienteSeleccionado.nombre, pacienteSeleccionado.apellido]
    .map((valor) => String(valor || '').trim())
    .filter(Boolean)
    .join(' ') || 'Paciente sin nombre';

  return (
    <div className="mx-auto max-w-6xl p-4 sm:p-6 lg:p-8">
      <div className="mb-6 flex flex-col gap-6 rounded-[28px] border border-surface-200/70 bg-gradient-to-r from-primary-50/80 via-white to-white p-5 md:flex-row md:items-end md:justify-between sm:p-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <button
            onClick={alVolver}
            className="inline-flex items-center gap-2 rounded-xl border border-outline-variant/20 bg-white px-4 py-2.5 text-sm font-medium text-on-surface transition-all duration-200 hover:bg-surface-container hover:border-outline-variant/40"
          >
            <span className="material-symbols-outlined text-base" aria-hidden>arrow_back</span>
            Volver
          </button>
          <h2 className="text-3xl font-extrabold tracking-tight text-on-surface">
            Detalle del paciente
          </h2>
        </div>
        <div className="text-sm text-on-surface-variant">
          Gestion de historia clinica, autorizaciones y cronograma terapeutico.
        </div>
      </div>

      <div className="mb-6 px-1" aria-label="Paciente seleccionado">
        <p className="mb-1 text-xs font-bold uppercase tracking-widest text-on-surface-variant">Paciente</p>
        <h3 className="m-0 break-words text-3xl font-extrabold tracking-tight text-primary sm:text-4xl">
          {nombreCompletoPaciente}
        </h3>
      </div>

      <div role="tablist" aria-label="Secciones del detalle del paciente" className="mb-5 flex gap-2 overflow-x-auto rounded-2xl border border-outline-variant/15 bg-surface-container-low p-2">
        {SECCIONES_DETALLE.map((seccion) => (
          <button key={seccion.id} type="button" role="tab" aria-selected={seccionDetalleActiva === seccion.id}
            onClick={() => setSeccionDetalleActiva(seccion.id)}
            className={`inline-flex min-w-max flex-1 items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-bold transition-all ${seccionDetalleActiva === seccion.id ? 'bg-primary text-on-primary shadow-md shadow-primary/20' : 'text-on-surface-variant hover:bg-surface-container-lowest hover:text-primary'}`}>
            <span className="material-symbols-outlined text-[20px]">{seccion.icon}</span>{seccion.label}
          </button>
        ))}
      </div>
      <fieldset disabled={soloLectura} className="min-w-0">
      <form onSubmit={guardarCambios} noValidate>
        <div role="tabpanel" className={`${seccionBase} ${seccionDetalleActiva === 'paciente' ? '' : 'hidden'}`}>
          <h4 className={tituloSeccion}>Paciente</h4>
          <div className="space-y-4">
            <div>
              <label className={labelBase}>Nombre</label>
              <input
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
                className={inputBase}
              />
            </div>
            <div>
              <label className={labelBase}>Apellido</label>
              <input
                value={apellido}
                onChange={(e) => setApellido(e.target.value)}
                className={inputBase}
              />
            </div>
            <div>
              <label className={labelBase}>Fecha de nacimiento</label>
              <InputFecha
                required
                max={new Date().toISOString().slice(0, 10)}
                value={fechaNacimiento}
                onChange={(e) => setFechaNacimiento(e.target.value)}
                className={inputBase}
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelBase}>Último control fisiatrico</label>
                <InputFecha
                  value={ultimoControlFisiatrico}
                  onChange={(e) => setUltimoControlFisiatrico(e.target.value)}
                  className={inputBase}
                />
              </div>
              <div>
                <label className={labelBase}>Vencimiento control fisiatrico</label>
                <InputFecha
                  value={fechaVencimientoControlFisiatrico}
                  onChange={(e) => setFechaVencimientoControlFisiatrico(e.target.value)}
                  className={inputBase}
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelBase}>Último control de trabajo social</label>
                <InputFecha
                  value={ultimoControlTrabajoSocial}
                  onChange={(e) => setUltimoControlTrabajoSocial(e.target.value)}
                  className={inputBase}
                />
              </div>
              <div>
                <label className={labelBase}>Vencimiento de trabajo social</label>
                <InputFecha
                  value={fechaVencimientoControlTrabajoSocial}
                  onChange={(e) => setFechaVencimientoControlTrabajoSocial(e.target.value)}
                  className={inputBase}
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className={labelBase}>DNI</label>
                <input
                  value={dni}
                  onChange={(e) =>
                    setDni(String(e.target.value || '').replace(/\D/g, '').slice(0, 8))
                  }
                  inputMode="numeric"
                  maxLength={8}
                  minLength={7}
                  required
                  className={inputBase}
                />
              </div>
              <div>
                <label className={labelBase}>CUIT</label>
                <input
                  value={cuit}
                  onChange={(e) => setCuit(e.target.value.replace(/\D/g, '').slice(0, 11))}
                  inputMode="numeric"
                  maxLength={11}
                  className={inputBase}
                />
              </div>
              <div>
                <label className={labelBase}>N° afiliado</label>
                <input value={nroAfiliado} onChange={(e) => setNroAfiliado(e.target.value)} className={inputBase} />
              </div>
            </div>
            <div>
              <label className={labelBase}>Diagnóstico</label>
              <input
                value={diagnostico}
                onChange={(e) => setDiagnostico(e.target.value)}
                list="diagnosticos-paciente"
                className={inputBase}
              />
              <datalist id="diagnosticos-paciente">
                {diagnosticosDisponibles.map((d) => (
                  <option key={`diag-p-${d}`} value={d} />
                ))}
              </datalist>
            </div>
            <div>
              <label className={labelBase}>Modulo</label>
              <div className="mt-2 flex flex-wrap gap-3">
                {MODULOS_PACIENTE.map((m) => (
                  <label
                    key={m.id}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-surface-200 hover:border-surface-300 cursor-pointer transition-colors duration-200"
                  >
                    <input
                      type="checkbox"
                      checked={modulo.includes(m.id)}
                      onChange={() =>
                        setModulo((prev) =>
                          prev.includes(m.id)
                            ? prev.filter((item) => item !== m.id)
                            : [...prev, m.id]
                        )
                      }
                      className="rounded border-surface-300 text-primary-600 focus:ring-primary-500"
                    />
                    {m.label}
                  </label>
                ))}
              </div>
            </div>
            <div className="pt-2 border-t border-surface-200">
              <h5 className="text-sm font-semibold text-surface-800 mb-3">Autorización</h5>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelBase}>Autorizado desde</label>
                  <InputFecha
                    value={autorizadoDesde}
                    onChange={(e) => setAutorizadoDesde(e.target.value)}
                    className={inputBase}
                  />
                </div>
                <div>
                  <label className={labelBase}>Vence el</label>
                  <InputFecha
                    value={autorizadoHasta}
                    onChange={(e) => setAutorizadoHasta(e.target.value)}
                    min={autorizadoDesde || undefined}
                    className={inputBase}
                  />
                </div>
              </div>
            </div>
            <div>
              <label className={labelBase}>CAR</label>
              <div className="mt-2 flex flex-wrap gap-3">
                {ANIOS_ESCOLARES.map((anio) => (
                  <label
                    key={`car-modulo-${anio}`}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-surface-200 hover:border-surface-300 cursor-pointer transition-colors duration-200"
                  >
                    <input
                      type="checkbox"
                      checked={carAnios.includes(anio)}
                      onChange={() =>
                        setCarAnios((prev) =>
                          prev.includes(anio)
                            ? prev.filter((item) => item !== anio)
                            : [...prev, anio]
                        )
                      }
                      className="rounded border-surface-300 text-primary-600 focus:ring-primary-500"
                    />
                    {anio}
                  </label>
                ))}
              </div>
            </div>
            <div>
              <label className={labelBase}>PPI</label>
              <div className="mt-2 flex flex-wrap gap-3">
                {ANIOS_ESCOLARES.map((anio) => (
                  <label
                    key={`ppi-modulo-${anio}`}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-surface-200 hover:border-surface-300 cursor-pointer transition-colors duration-200"
                  >
                    <input
                      type="checkbox"
                      checked={ppiAnios.includes(anio)}
                      onChange={() =>
                        setPpiAnios((prev) =>
                          prev.includes(anio)
                            ? prev.filter((item) => item !== anio)
                            : [...prev, anio]
                        )
                      }
                      className="rounded border-surface-300 text-primary-600 focus:ring-primary-500"
                    />
                    {anio}
                  </label>
                ))}
              </div>
            </div>
            <div>
              <label className={labelBase}>Acta Acuerdo</label>
              <div className="mt-2 flex flex-wrap gap-3">
                {ANIOS_ESCOLARES.map((anio) => (
                  <label
                    key={`acta-acuerdo-modulo-${anio}`}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-surface-200 hover:border-surface-300 cursor-pointer transition-colors duration-200"
                  >
                    <input
                      type="checkbox"
                      checked={actaAcuerdoAnios.includes(anio)}
                      onChange={() =>
                        setActaAcuerdoAnios((prev) =>
                          prev.includes(anio)
                            ? prev.filter((item) => item !== anio)
                            : [...prev, anio]
                        )
                      }
                      className="rounded border-surface-300 text-primary-600 focus:ring-primary-500"
                    />
                    {anio}
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>
        <div role="tabpanel" className={`${seccionBase} ${seccionDetalleActiva === 'familia' ? '' : 'hidden'}`}>
          <h4 className={tituloSeccion}>Escuela</h4>
          <div className="space-y-4">
            <div>
              <label className={labelBase}>Escuela</label>
              <input
                value={escuela}
                onChange={(e) => setEscuela(e.target.value)}
                className={inputBase}
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelBase}>Año/Grado</label>
                <input
                  value={anioGrado}
                  onChange={(e) => setAnioGrado(e.target.value)}
                  className={inputBase}
                />
              </div>
              <div>
                <label className={labelBase}>Turno escolar</label>
                <select
                  value={turnoEscolar}
                  onChange={(e) => setTurnoEscolar(e.target.value)}
                  className={inputBase}
                >
                  <option value="">(Seleccionar)</option>
                  <option value="manana">Mañana</option>
                  <option value="tarde">Tarde</option>
                </select>
              </div>
            </div>
          </div>
        </div>
        <div className={`${seccionBase} ${seccionDetalleActiva === 'familia' ? '' : 'hidden'}`}>
          <h4 className={tituloSeccion}>Padres</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelBase}>Padre/Tutor</label>
              <input
                value={padreTutor}
                onChange={(e) => setPadreTutor(e.target.value)}
                className={inputBase}
              />
            </div>
            <div>
              <label className={labelBase}>Teléfono Padre/Tutor</label>
              <input
                value={telefonoPadreTutor}
                onChange={(e) => setTelefonoPadreTutor(e.target.value)}
                className={inputBase}
              />
            </div>
            <div>
              <label className={labelBase}>Madre/Tutora</label>
              <input
                value={madreTutora}
                onChange={(e) => setMadreTutora(e.target.value)}
                className={inputBase}
              />
            </div>
            <div>
              <label className={labelBase}>Teléfono Madre/Tutora</label>
              <input
                value={telefonoMadreTutora}
                onChange={(e) => setTelefonoMadreTutora(e.target.value)}
                className={inputBase}
              />
            </div>
          </div>
        </div>
        <div className={`${seccionBase} ${seccionDetalleActiva === 'familia' ? '' : 'hidden'}`}>
          <h4 className={tituloSeccion}>Domicilio</h4>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="sm:col-span-2">
              <label className={labelBase}>Calle</label>
              <input
                value={calle}
                onChange={(e) => setCalle(e.target.value.slice(0, 160))}
                required
                maxLength={160}
                className={inputBase}
              />
            </div>
            <div>
              <label className={labelBase}>Numeración</label>
              <input
                value={numeracion}
                onChange={(e) => setNumeracion(e.target.value)}
                className={inputBase}
              />
            </div>
            <div>
              <label className={labelBase}>Barrio</label>
              <input
                value={barrio}
                onChange={(e) => setBarrio(e.target.value)}
                className={inputBase}
              />
            </div>
            <div>
              <label className={labelBase}>Piso</label>
              <input
                value={piso}
                onChange={(e) => setPiso(e.target.value)}
                className={inputBase}
              />
            </div>
            <div>
              <label className={labelBase}>Sector</label>
              <input
                value={sector}
                onChange={(e) => setSector(e.target.value)}
                className={inputBase}
              />
            </div>
          </div>
        </div>

        <div role="tabpanel" className={`${seccionBase} ${seccionDetalleActiva === 'cobertura' ? '' : 'hidden'}`}>
          <h4 className={tituloSeccion}>Obra social</h4>
          <div className="space-y-4">
            <div>
              <label className={labelBase}>Obra social seleccionada</label>
              <input
                value={obraSocial}
                readOnly
                placeholder="Seleccioná una opción de la lista inferior"
                className={inputBase}
              />
            </div>
            <div>
              <label className={labelBase}>Lista de obras sociales (selección obligatoria)</label>
              <select
                value={String(obraSocialPdfId || '').trim()}
                onChange={(e) => {
                  const val = e.target.value;
                  setObraSocial(val);
                  setObraSocialPdfId(val);
                }}
                className={inputBase}
              >
                <option value="">(Seleccionar)</option>
                {ordenarObrasSociales.map((o) => (
                <option key={`os-${o.id}`} value={o.id}>
                    {o.id}{o.hasTemplate === false ? ' (sin plantilla)' : ''}
                </option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className={labelBase}>Tratamiento (planilla)</label>
                <select
                  value={tratamientoOs}
                  onChange={(e) => setTratamientoOs(e.target.value)}
                  className={inputBase}
                >
                  <option value="">(Seleccionar)</option>
                  {(pacienteSeleccionado.tratamientos || []).map((t) => (
                    <option key={`t-os-${t}`} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelBase}>Mes (planilla)</label>
                <select
                  value={String(mesOs)}
                  onChange={(e) => setMesOs(Number(e.target.value))}
                  className={inputBase}
                >
                  {MESES.map((m, idx) => (
                    <option key={`mes-os-${idx + 1}`} value={idx + 1}>
                      {m}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelBase}>Año (planilla)</label>
                <input
                  value={String(anioOs)}
                  onChange={(e) => setAnioOs(e.target.value)}
                  className={inputBase}
                />
              </div>
            </div>
            <button
              type="button"
              onClick={descargarObraSocial}
              className="px-4 py-2.5 rounded-xl text-sm font-medium text-white border border-surface-200 hover:bg-surface-100 transition-colors duration-200"
            >
              Descargar PDF de obra social
            </button>
          </div>
        </div>

        <div className={`flex flex-wrap items-center gap-3 mb-6 ${['paciente', 'familia', 'cobertura', 'horarios'].includes(seccionDetalleActiva) ? '' : 'hidden'}`}>
          {!soloLectura && <button
            type="submit"
            className="px-4 py-2.5 rounded-xl text-sm font-medium bg-primary-600 text-white hover:bg-primary-500 active:bg-primary-600 transition-colors duration-200"
          >
            Guardar cambios
          </button>}
          {usuario?.isAdmin ? (
            <button
              type="button"
              onClick={confirmarEliminacion}
              className="px-4 py-2.5 rounded-xl text-sm font-medium border border-rose-200 text-rose-700 hover:bg-rose-50 hover:border-rose-300 transition-colors duration-200"
            >
              Eliminar paciente
            </button>
          ) : null}
          {guardado ? (
            <span className="text-primary-600 font-medium text-sm">Cambios guardados.</span>
          ) : null}
          {errorGuardado ? (
            <span className="text-rose-600 font-medium text-sm">{errorGuardado}</span>
          ) : null}
        </div>
      </form>

      <div role="tabpanel" className={`${seccionBase} ${seccionDetalleActiva === 'tratamientos' ? '' : 'hidden'}`}>
        <h3 className="text-base font-semibold text-surface-900 mb-2">Tratamientos</h3>
        <p className="text-surface-500 text-sm mb-4">
          Marca o desmarca tratamientos para este paciente.
        </p>
        <div className="flex flex-wrap gap-2 mb-6">
          {tratamientosDisponibles.map((t) => {
            const checked = (pacienteSeleccionado.tratamientos || []).includes(t);
            return (
              <label
                key={`trat-${t}`}
                className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-colors duration-200 ${
                  checked ? 'border-primary-300 bg-primary-50 text-primary-800' : 'border-surface-200 hover:border-surface-300'
                }`}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={(e) => alternarTratamientoPaciente(t, e.target.checked)}
                  className="rounded border-surface-300 text-primary-600 focus:ring-primary-500"
                />
                {t}
              </label>
            );
          })}
        </div>

        <h3 className="text-base font-semibold text-surface-900 mb-3">Tratamientos y meses</h3>
        {(pacienteSeleccionado.tratamientos || []).length ? (
          <div className="flex flex-wrap gap-3">
            {pacienteSeleccionado.tratamientos.map((t) => {
              const resumen = resumenTurnos[t];
              return (
                <div
                  key={t}
                  className="flex flex-wrap items-center gap-3 p-4 rounded-xl border border-surface-200 bg-surface-50/50"
                >
                  <strong className="text-surface-800">{t}</strong>
                  <span className="text-surface-600 text-sm">
                    {resumen && resumen.dias.size
                      ? `Días: ${ordenarDiasResumen(resumen.dias).join(', ')}`
                      : 'Días: -'}
                  </span>
                  <span className="text-surface-600 text-sm">
                    {resumen && resumen.horas.size
                      ? `Horarios: ${Array.from(resumen.horas).sort().join(', ')}`
                      : 'Horarios: -'}
                  </span>
                  <button
                    type="button"
                    onClick={() => confirmarQuitarTratamiento(t)}
                    className="ml-auto px-3 py-1.5 rounded-lg text-sm font-medium text-rose-600 hover:bg-rose-50 transition-colors duration-200"
                  >
                    Quitar
                  </button>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-surface-500 text-sm">Sin tratamientos.</p>
        )}
      </div>

      <div role="tabpanel" className={`${seccionBase} ${seccionDetalleActiva === 'solicitudes' ? '' : 'hidden'}`}>
        <h3 className="text-base font-semibold text-surface-900 mb-2">Solicitud de autorización</h3>
        <p className="text-surface-500 text-sm mb-4">
          Registra el período solicitado y si la obra social ya lo autorizó. Solo una solicitud
          autorizada se activará al comenzar; al vencer, el paciente quedará no autorizado salvo que
          comience otra autorización aprobada.
        </p>
        <div className="mb-4">
          <div className="text-sm text-surface-600 mb-2">
            <strong>Tratamientos actuales:</strong>{' '}
            {(pacienteSeleccionado.tratamientos || []).length
              ? (pacienteSeleccionado.tratamientos || []).join(', ')
              : '-'}
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={usarMismosTratamientosSolicitud}
              className={`px-3 py-2 rounded-lg text-sm font-medium border transition-colors duration-200 ${
                modoSolicitudTratamientos === 'mismos'
                  ? 'border-primary-300 bg-primary-50 text-primary-800'
                  : 'border-surface-200 text-surface-700 hover:bg-surface-100'
              }`}
            >
              Mismos tratamientos
            </button>
            <button
              type="button"
              onClick={usarNuevosTratamientosSolicitud}
              className={`px-3 py-2 rounded-lg text-sm font-medium border transition-colors duration-200 ${
                modoSolicitudTratamientos === 'nuevos'
                  ? 'border-primary-300 bg-primary-50 text-primary-800'
                  : 'border-surface-200 text-surface-700 hover:bg-surface-100'
              }`}
            >
              Nuevos tratamientos
            </button>
          </div>
        </div>
        {panelSolicitudAbierto && (
        <>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
          <div>
            <label className={labelBase}>Comienza la autorización</label>
            <InputFecha
              value={solicitudFechaInicio}
              onChange={(e) => setSolicitudFechaInicio(e.target.value)}
              className={inputBase}
            />
          </div>
          <div>
            <label className={labelBase}>Vence la autorización</label>
            <InputFecha
              value={solicitudFechaFin}
              onChange={(e) => setSolicitudFechaFin(e.target.value)}
              className={inputBase}
            />
          </div>
          <div>
            <label className={labelBase}>Estado de la autorización</label>
            <select
              value={estadoAutorizacionSolicitud}
              onChange={(e) => setEstadoAutorizacionSolicitud(e.target.value)}
              className={inputBase}
            >
              <option value="pendiente">Todavía no autorizada</option>
              <option value="autorizada">Autorizada</option>
            </select>
          </div>
        </div>
        <div className="mb-4">
          <label className={labelBase}>Terapias de la solicitud</label>
          {modoSolicitudTratamientos === 'mismos' ? (
            <p className="text-surface-600 text-sm">
              Se usarán los tratamientos actuales del paciente para esta solicitud.
            </p>
          ) : tratamientosSolicitudDisponibles.length ? (
            <div className="flex flex-wrap gap-2">
              {tratamientosSolicitudDisponibles.map((t) => {
                const checked = solicitudTratamientos.has(t);
                return (
                  <label
                    key={`sol-${t}`}
                    className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-colors duration-200 ${
                      checked
                        ? 'border-primary-300 bg-primary-50 text-primary-800'
                        : 'border-surface-200 hover:border-surface-300'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => alternarTratamientoSolicitud(t)}
                      className="rounded border-surface-300 text-primary-600 focus:ring-primary-500"
                    />
                    {t}
                  </label>
                );
              })}
            </div>
          ) : (
            <p className="text-surface-500 text-sm">
              Sin terapias disponibles.
            </p>
          )}
        </div>
        <div className="mb-4 rounded-xl border border-primary-100 bg-primary-50/40 p-4">
          <div className="mb-3">
            <label className={labelBase}>Horarios propios de esta solicitud</label>
            <p className="text-sm text-surface-600">
              Estos horarios quedan separados del cronograma vigente. Solo lo reemplazarán cuando la
              solicitud esté autorizada y llegue su fecha de inicio.
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[680px] table-fixed border-collapse">
              <thead>
                <tr>
                  <th className="p-2 text-left text-xs font-semibold text-surface-600">Terapia</th>
                  {DIAS.map((dia) => (
                    <th key={`solicitud-dia-${dia}`} className="p-2 text-left text-xs font-semibold text-surface-600">
                      {ETIQUETAS_DIAS[dia] || dia}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(modoSolicitudTratamientos === 'mismos'
                  ? pacienteSeleccionado.tratamientos || []
                  : Array.from(solicitudTratamientos)
                ).filter((tratamiento) => tratamiento !== 'Integracion').map((tratamiento) => (
                  <tr key={`horario-solicitud-${tratamiento}`} className="border-t border-primary-100">
                    <td className="p-2 text-sm font-medium text-surface-800">{tratamiento}</td>
                    {DIAS.map((dia) => (
                      <td key={`${tratamiento}-${dia}`} className="p-2">
                        <select
                          value={solicitudHorarios?.[tratamiento]?.[dia] || ''}
                          onChange={(e) =>
                            actualizarHoraSolicitud(
                              setSolicitudHorarios,
                              tratamiento,
                              dia,
                              e.target.value
                            )
                          }
                          className="w-full rounded-lg border border-surface-200 bg-white px-2 py-2 text-xs"
                        >
                          <option value="">Sin turno</option>
                          {horariosDisponiblesPaciente.map((hora) => (
                            <option key={`${tratamiento}-${dia}-${hora}`} value={hora}>
                              {hora}
                            </option>
                          ))}
                        </select>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        {modoSolicitudTratamientos === 'nuevos' ? (
          <label className="inline-flex items-center gap-2 text-sm text-surface-700 mb-4">
            <input
              type="checkbox"
              checked={aplicarCambiosSolicitud}
              onChange={(e) => setAplicarCambiosSolicitud(e.target.checked)}
              className="rounded border-surface-300 text-primary-600 focus:ring-primary-500"
            />
            Aplicar nuevos tratamientos automáticamente desde la fecha de inicio
          </label>
        ) : null}
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <button
            type="button"
            onClick={guardarSolicitud}
            disabled={guardandoSolicitud}
            className="px-4 py-2.5 rounded-xl text-sm font-medium bg-primary-600 text-white hover:bg-primary-500 active:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed transition-colors duration-200"
          >
            {guardandoSolicitud ? 'Guardando autorización...' : 'Guardar autorización'}
          </button>
          <button
            type="button"
            onClick={() => { setPanelSolicitudAbierto(false); setMensajeSolicitud(''); setErrorSolicitud(''); }}
            className="px-4 py-2.5 rounded-xl text-sm font-medium border border-surface-200 text-surface-700 hover:bg-surface-100 transition-colors duration-200"
          >
            Cancelar
          </button>
          {mensajeSolicitud ? (
            <span className="text-primary-600 font-medium text-sm">{mensajeSolicitud}</span>
          ) : null}
          {errorSolicitud ? (
            <span className="text-rose-600 font-medium text-sm">{errorSolicitud}</span>
          ) : null}
        </div>
        </>
        )}
        <div className="pt-4 border-t border-surface-200">
          <h4 className="text-sm font-semibold text-surface-800 mb-3">
            Historial de solicitudes (últimos 12 meses)
          </h4>
          {historialSolicitudes.length ? (
            <div className="space-y-2">
              {historialSolicitudes.map((sol) => (
                <div
                  key={`hist-sol-${sol.id}`}
                  className="rounded-lg border border-surface-200 bg-surface-50/60 p-3"
                >
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <strong className="text-surface-800">
                      {formatearFechaArgentina(sol.fechaInicio)} al {formatearFechaArgentina(sol.fechaFin)}
                    </strong>
                    <span className="text-xs px-2 py-0.5 rounded-full border border-surface-200 text-surface-600 bg-white">
                      {sol.vigente ? 'Vigente' : sol.futura ? 'Programada' : 'Vencida'}
                    </span>
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full border ${
                        sol.estadoAutorizacion === 'autorizada'
                          ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                          : 'border-amber-200 bg-amber-50 text-amber-800'
                      }`}
                    >
                      {sol.estadoAutorizacion === 'autorizada'
                        ? 'Autorizada'
                        : 'Pendiente de autorización'}
                    </span>
                  </div>
                  <div className="text-sm text-surface-600 mb-3">
                    Terapias: {(sol.tratamientos || []).length ? sol.tratamientos.join(', ') : '-'}
                  </div>
                  <div className="mb-3 text-sm text-surface-600">
                    <strong>Horarios:</strong>{' '}
                    {describirHorarioSemanal(sol.turnosPorMes, sol.tratamientos || []) ||
                      'Todavía no configurados'}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      disabled={solicitudActualizandoId === sol.id}
                      onClick={() =>
                        cambiarEstadoAutorizacionSolicitud(
                          sol,
                          sol.estadoAutorizacion === 'autorizada' ? 'pendiente' : 'autorizada'
                        )
                      }
                      className={`rounded-lg px-3 py-2 text-xs font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
                        sol.estadoAutorizacion === 'autorizada'
                          ? 'bg-amber-100 text-amber-800 hover:bg-amber-200'
                          : 'bg-emerald-600 text-white hover:bg-emerald-500'
                      }`}
                    >
                      {solicitudActualizandoId === sol.id
                        ? 'Actualizando...'
                        : sol.estadoAutorizacion === 'autorizada'
                        ? 'Marcar como pendiente'
                        : 'Marcar como autorizada'}
                    </button>
                    <button
                      type="button"
                      onClick={() => abrirEditorHorariosSolicitud(sol)}
                      className="rounded-lg border border-primary-200 bg-white px-3 py-2 text-xs font-semibold text-primary-700 hover:bg-primary-50"
                    >
                      {solicitudEditandoId === sol.id ? 'Cerrar horarios' : 'Editar horarios'}
                    </button>
                    {usuario?.isAdmin ? (
                      <>
                        <button
                          type="button"
                          disabled={
                            guardandoFechasSolicitud || eliminandoSolicitudId === sol.id
                          }
                          onClick={() => abrirEditorFechasSolicitud(sol)}
                          className="rounded-lg border border-surface-300 bg-white px-3 py-2 text-xs font-semibold text-surface-700 hover:bg-surface-100 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          {solicitudFechasEditandoId === sol.id
                            ? 'Cerrar fechas'
                            : 'Editar fechas'}
                        </button>
                        <button
                          type="button"
                          disabled={eliminandoSolicitudId === sol.id || guardandoFechasSolicitud}
                          onClick={() => confirmarEliminarSolicitud(sol)}
                          className="rounded-lg border border-rose-200 bg-white px-3 py-2 text-xs font-semibold text-rose-700 hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          {eliminandoSolicitudId === sol.id
                            ? 'Eliminando...'
                            : 'Eliminar solicitud'}
                        </button>
                      </>
                    ) : null}
                  </div>
                  {usuario?.isAdmin && solicitudFechasEditandoId === sol.id ? (
                    <div className="mt-4 rounded-xl border border-surface-200 bg-white p-3">
                      <p className="mb-3 text-xs text-surface-600">
                        Solo administradores pueden corregir estas fechas. El cronograma de la
                        solicitud se ajustará automáticamente al nuevo período.
                      </p>
                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <div>
                          <label className={labelBase}>Comienza la autorización</label>
                          <InputFecha
                            value={solicitudFechaInicioEditando}
                            onChange={(e) => setSolicitudFechaInicioEditando(e.target.value)}
                            aria-invalid={Boolean(errorSolicitud)}
                            className={inputBase}
                          />
                        </div>
                        <div>
                          <label className={labelBase}>Vence la autorización</label>
                          <InputFecha
                            value={solicitudFechaFinEditando}
                            onChange={(e) => setSolicitudFechaFinEditando(e.target.value)}
                            min={solicitudFechaInicioEditando || undefined}
                            aria-invalid={Boolean(errorSolicitud)}
                            className={inputBase}
                          />
                        </div>
                      </div>
                      {errorSolicitud ? (
                        <div
                          role="alert"
                          className="mt-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-semibold text-rose-700"
                        >
                          {errorSolicitud}
                        </div>
                      ) : null}
                      <div className="mt-3 flex flex-wrap gap-2">
                        <button
                          type="button"
                          disabled={guardandoFechasSolicitud}
                          onClick={() => guardarFechasSolicitud(sol)}
                          className="rounded-lg bg-primary-600 px-3 py-2 text-xs font-semibold text-white hover:bg-primary-500 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          {guardandoFechasSolicitud ? 'Guardando...' : 'Guardar fechas'}
                        </button>
                        <button
                          type="button"
                          disabled={guardandoFechasSolicitud}
                          onClick={() => abrirEditorFechasSolicitud(sol)}
                          className="rounded-lg border border-surface-200 bg-white px-3 py-2 text-xs font-semibold text-surface-600 hover:bg-surface-50 disabled:opacity-60"
                        >
                          Cancelar
                        </button>
                      </div>
                    </div>
                  ) : null}
                  {solicitudEditandoId === sol.id ? (
                    <div className="mt-4 rounded-xl border border-primary-100 bg-white p-3">
                      <p className="mb-3 text-xs text-surface-600">
                        Los cambios quedan guardados únicamente en esta solicitud.
                      </p>
                      <div className="overflow-x-auto">
                        <table className="w-full min-w-[680px] table-fixed border-collapse">
                          <thead>
                            <tr>
                              <th className="p-2 text-left text-xs font-semibold text-surface-600">Terapia</th>
                              {DIAS.map((dia) => (
                                <th key={`edit-${sol.id}-${dia}`} className="p-2 text-left text-xs font-semibold text-surface-600">
                                  {ETIQUETAS_DIAS[dia] || dia}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {(sol.tratamientos || []).filter((tratamiento) => tratamiento !== 'Integracion').map((tratamiento) => (
                              <tr key={`edit-${sol.id}-${tratamiento}`} className="border-t border-surface-100">
                                <td className="p-2 text-sm font-medium text-surface-800">{tratamiento}</td>
                                {DIAS.map((dia) => (
                                  <td key={`edit-${sol.id}-${tratamiento}-${dia}`} className="p-2">
                                    <select
                                      value={solicitudHorariosEditando?.[tratamiento]?.[dia] || ''}
                                      onChange={(e) =>
                                        actualizarHoraSolicitud(
                                          setSolicitudHorariosEditando,
                                          tratamiento,
                                          dia,
                                          e.target.value
                                        )
                                      }
                                      className="w-full rounded-lg border border-surface-200 bg-white px-2 py-2 text-xs"
                                    >
                                      <option value="">Sin turno</option>
                                      {horariosDisponiblesPaciente.map((hora) => (
                                        <option key={`edit-${sol.id}-${tratamiento}-${dia}-${hora}`} value={hora}>
                                          {hora}
                                        </option>
                                      ))}
                                    </select>
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <button
                        type="button"
                        disabled={guardandoHorariosSolicitud}
                        onClick={() => guardarEditorHorariosSolicitud(sol)}
                        className="mt-3 rounded-lg bg-primary-600 px-3 py-2 text-xs font-semibold text-white hover:bg-primary-500 disabled:opacity-60"
                      >
                        {guardandoHorariosSolicitud ? 'Guardando...' : 'Guardar horarios de la solicitud'}
                      </button>
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-surface-500 text-sm">Sin solicitudes registradas en el último año.</p>
          )}
        </div>
      </div>

      <div role="tabpanel" className={`${seccionBase} ${seccionDetalleActiva === 'horarios' ? '' : 'hidden'}`}>
        <button
          type="button"
          onClick={() => setMostrarCronogramaBase((prev) => !prev)}
          className="w-full sm:w-auto px-4 py-2.5 rounded-xl text-sm font-medium bg-surface-100 text-surface-700 hover:bg-surface-200 border border-surface-200 transition-colors duration-200"
        >
          {mostrarCronogramaBase
            ? 'Ocultar Cronograma general'
            : solicitudObjetivoCronograma
            ? 'Cronograma de la próxima solicitud'
            : 'Cronograma general'}
        </button>
        {mostrarCronogramaBase ? (
          <div className="mt-4 space-y-4">
            {solicitudObjetivoCronograma ? (
              <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                <strong>Editando la solicitud del{' '}
                  {formatearFechaArgentina(solicitudObjetivoCronograma.fechaInicio)} al{' '}
                  {formatearFechaArgentina(solicitudObjetivoCronograma.fechaFin)}.</strong>{' '}
                Los cambios se guardarán en esa solicitud y no modificarán el cronograma vigente.
              </div>
            ) : (
              <div className="rounded-xl border border-primary-100 bg-primary-50 px-4 py-3 text-sm text-primary-900">
                Estás editando el cronograma vigente del paciente.
              </div>
            )}
            {tratamientosCronograma.length ? (
              tratamientosCronograma.map((t) => (
                <div key={`cronograma-${t}`} className="pt-4 border-t border-surface-200 first:pt-0 first:border-t-0">
                  <strong className="text-surface-800">{t}</strong>
                  {t === 'Integracion' ? (
                    <div className="mt-3">
                      <label className={labelBase}>Horario de Integración (texto libre)</label>
                      <textarea
                        value={integracionHorario}
                        onChange={(e) => setIntegracionHorario(e.target.value)}
                        rows={3}
                        className={`${inputBase} resize-y`}
                        placeholder="Ej: Lunes y Miércoles 14:00 a 15:00"
                      />
                    </div>
                  ) : (
                    <div className="overflow-x-auto mt-3">
                      <table className="w-full min-w-[500px] table-fixed border-collapse">
                        <thead>
                          <tr>
                            <th className="text-left p-2 text-sm font-medium text-surface-600">Hora</th>
                            {DIAS.map((d) => (
                              <th key={`dia-${d}`} className="text-left p-2 text-sm font-medium text-surface-600">
                                {ETIQUETAS_DIAS[d] || d}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {horariosDisponiblesPaciente.map((hora) => (
                            <tr key={`hora-${hora}`}>
                              <td className="p-2 whitespace-nowrap text-sm text-surface-600">
                                {hora}
                              </td>
                              {DIAS.map((dia) => {
                                const clave = `${dia}-${hora}`;
                                const marcado = obtenerMarcadoGeneral(t, clave);
                                return (
                                  <td key={clave} className="p-2">
                                    <input
                                      type="checkbox"
                                      checked={marcado}
                                      onChange={() => alternarDraftGeneral(t, clave)}
                                      className="rounded border-surface-300 text-primary-600 focus:ring-primary-500"
                                    />
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ))
            ) : (
              <p className="text-surface-500 text-sm">Asigna tratamientos para habilitar el cronograma.</p>
            )}
            <div className="flex flex-wrap items-center gap-3 pt-4">
              <button
                type="button"
                onClick={guardarCronogramaGeneral}
                disabled={!hayCambiosGeneral || guardandoCronogramaGeneral}
                className="px-4 py-2.5 rounded-xl text-sm font-medium bg-primary-600 text-white hover:bg-primary-500 active:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
              >
                {guardandoCronogramaGeneral
                  ? 'Guardando...'
                  : solicitudObjetivoCronograma
                  ? 'Guardar cronograma de la solicitud'
                  : 'Guardar cronograma vigente'}
              </button>
              <button
                type="button"
                onClick={descartarCronogramaGeneral}
                disabled={!hayCambiosGeneral}
                className="px-4 py-2.5 rounded-xl text-sm font-medium border border-surface-200 text-surface-700 hover:bg-surface-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
              >
                Descartar cambios
              </button>
              {hayCambiosGeneral ? (
                <span className="text-surface-500 text-sm">Cambios pendientes sin guardar.</span>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>

      <div className={`${seccionBase} ${seccionDetalleActiva === 'horarios' ? '' : 'hidden'}`}>
        <button
          type="button"
          onClick={() => setMostrarHorarios((prev) => !prev)}
          className="w-full sm:w-auto px-4 py-2.5 rounded-xl text-sm font-medium bg-surface-100 text-surface-700 hover:bg-surface-200 border border-surface-200 transition-colors duration-200"
        >
          {mostrarHorarios ? 'Ocultar horarios' : 'Configurar horarios por fecha'}
        </button>

        {mostrarHorarios ? (
          <div className="mt-4">
            <h3 className="text-base font-semibold text-surface-900 mb-3">Horarios por fecha (excepciones)</h3>
            <div className="mb-4">
              <div className="text-sm font-medium text-surface-700 mb-2">Meses</div>
              <div className="flex flex-wrap gap-2">
                {MESES.map((mesNombre, idx) => {
                  const mes = idx + 1;
                  return (
                    <label
                      key={mes}
                      className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-surface-200 hover:border-surface-300 cursor-pointer transition-colors"
                    >
                      <input
                        type="checkbox"
                        checked={mesesSeleccionados.has(mes)}
                        onChange={() => alternarMesSeleccionado(mes)}
                        className="rounded border-surface-300 text-primary-600 focus:ring-primary-500"
                      />
                      {mesNombre}
                    </label>
                  );
                })}
              </div>
            </div>
            {(pacienteSeleccionado.tratamientos || []).length ? (
            <div className="space-y-8">
              {!mesesSeleccionados.size ? (
                <p className="text-surface-500 text-sm">
                  Selecciona al menos un mes para ver y editar horarios.
                </p>
              ) : null}
              {MESES.map((mesNombre, idx) => {
                const mes = idx + 1;
                if (!mesesSeleccionados.has(mes)) return null;
                const semanas = obtenerCalendarioMes(mes);
                return (
                  <div key={mes} className="rounded-xl border border-surface-200 bg-white overflow-hidden shadow-sm">
                    <div className="px-4 py-3 bg-surface-50 border-b border-surface-200">
                      <h4 className="font-semibold text-surface-800">{mesNombre}</h4>
                    </div>
                    <div className="p-4 space-y-6">
                      {(pacienteSeleccionado.tratamientos || []).map((t) => (
                        <div key={t} className="rounded-lg border border-surface-200 bg-surface-50/50 overflow-hidden">
                          <div className="px-3 py-2 bg-surface-100/80 border-b border-surface-200 text-sm font-medium text-surface-700">
                            {t}
                          </div>
                          <div className="overflow-x-auto">
                            <table className="w-full min-w-[520px] border-collapse">
                              <thead>
                                <tr className="bg-surface-100">
                                  <th className="text-left p-2.5 text-xs font-semibold text-surface-600 border-b border-r border-surface-200 w-14">Lun</th>
                                  <th className="text-left p-2.5 text-xs font-semibold text-surface-600 border-b border-r border-surface-200 w-14">Mar</th>
                                  <th className="text-left p-2.5 text-xs font-semibold text-surface-600 border-b border-r border-surface-200 w-14">Mie</th>
                                  <th className="text-left p-2.5 text-xs font-semibold text-surface-600 border-b border-r border-surface-200 w-14">Jue</th>
                                  <th className="text-left p-2.5 text-xs font-semibold text-surface-600 border-b border-surface-200 w-14">Viernes</th>
                                </tr>
                              </thead>
                              <tbody>
                                {semanas.map((semana, idxSemana) => (
                                  <tr key={`${mes}-semana-${idxSemana}`} className="border-b border-surface-100 last:border-b-0">
                                    {semana.map((diaNumero, idxDia) => (
                                      <td
                                        key={`${mes}-${idxSemana}-${idxDia}`}
                                        className="p-2 align-top border-r border-surface-100 last:border-r-0 min-w-[96px] max-w-[132px]"
                                      >
                                        {diaNumero ? (
                                          (() => {
                                            const horasDia = obtenerHorasDia(mes, t, diaNumero);
                                            const disponibles = horariosDisponiblesPaciente.filter(
                                              (h) => !horasDia.includes(h)
                                            );
                                            const fechaStr = `${ANIO_BASE}-${String(mes).padStart(2, '0')}-${String(
                                              diaNumero
                                            ).padStart(2, '0')}`;
                                            return (
                                              <div className="space-y-1.5">
                                                <span className="inline-block text-xs font-semibold text-surface-700">
                                                  {diaNumero}
                                                </span>
                                                {horasDia.length ? (
                                                  <div className="space-y-1">
                                                    {horasDia.map((hora) => {
                                                      const overrideActual =
                                                        overridesMap[fechaStr] &&
                                                        overridesMap[fechaStr][t] &&
                                                        overridesMap[fechaStr][t][hora];
                                                      const activo =
                                                        overrideActual === undefined
                                                          ? true
                                                          : overrideActual;
                                                      return (
                                                        <label
                                                          key={`${diaNumero}-${hora}`}
                                                          className="flex items-center gap-1.5 text-xs cursor-pointer hover:bg-surface-100 rounded px-1 py-0.5 -mx-1"
                                                        >
                                                          <input
                                                            type="checkbox"
                                                            checked={activo}
                                                            onChange={(e) =>
                                                              alternarExcepcion(
                                                                mes,
                                                                t,
                                                                diaNumero,
                                                                hora,
                                                                e.target.checked
                                                              )
                                                            }
                                                            className="rounded border-surface-300 text-primary-600 focus:ring-primary-500 shrink-0"
                                                          />
                                                          <span className="truncate">{hora}</span>
                                                        </label>
                                                      );
                                                    })}
                                                  </div>
                                                ) : (
                                                  <span className="text-surface-400 text-xs">Sin horarios</span>
                                                )}
                                                <select
                                                  defaultValue=""
                                                  onChange={(e) => {
                                                    const hora = e.target.value;
                                                    if (!hora) return;
                                                    alternarExcepcion(
                                                      mes,
                                                      t,
                                                      diaNumero,
                                                      hora,
                                                      true
                                                    );
                                                    e.target.value = '';
                                                  }}
                                                  className="w-full text-xs px-2 py-1 rounded border border-surface-200 bg-white mt-0.5"
                                                >
                                                  <option value="">+ Agregar</option>
                                                  {disponibles.map((h) => (
                                                    <option key={h} value={h}>
                                                      {h}
                                                    </option>
                                                  ))}
                                                </select>
                                              </div>
                                            );
                                          })()
                                        ) : (
                                          <span className="text-surface-300 text-xs">—</span>
                                        )}
                                      </td>
                                    ))}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-surface-500 text-sm">Asigna tratamientos para habilitar el cronograma.</p>
          )}
          </div>
        ) : null}
      </div>
      </fieldset>
    </div>
  );
}
